/**
 * Document entity: backend DTOs, API calls, and the DTO -> display mapper.
 *
 * The DocumentDto is defined once in src/lib/uploads.ts (which owns the upload
 * flow) and re-exported here so callers can import it from the entity module
 * alongside the mapper. The actual file upload stays in uploads.ts
 * (uploadDocumentFile / createDocument / ...) — do not duplicate it.
 */

import type { CompanyDocument } from '../../app/types';
import { apiGet, apiPost } from '../apiClient';
import { getAuthToken } from '../auth';
import type { DocumentDto } from '../uploads';
import { categoryToKebab, formatDate, statusToKebab } from './format';

/** API base, mirroring apiClient: empty in dev (Vite proxies /api), set in prod. */
const API_BASE = import.meta.env.VITE_API_BASE_URL?.replace(/\/$/, '') ?? '';

export type { DocumentDto };

export interface CreateDocumentRequest {
  companyId: string;
  name: string;
  catalogId?: string;
  employeeId?: string;
  caseId?: string;
  summary?: string;
  category?: string;
}

/** GET /api/documents?companyId={id}. */
export function listDocuments(companyId: string): Promise<DocumentDto[]> {
  return apiGet<DocumentDto[]>(`/api/documents?companyId=${encodeURIComponent(companyId)}`);
}

/** GET /api/documents/{id}. */
export function getDocument(id: string): Promise<DocumentDto> {
  return apiGet<DocumentDto>(`/api/documents/${id}`);
}

/**
 * A short-lived read ticket for a document's current file: a plain GET on `url`
 * downloads the bytes directly from storage. The url expires within minutes —
 * fetch a fresh ticket per view/download, never store one.
 */
export interface DocumentFileTicketDto {
  url: string;
  expiresAt: string;
  originalName: string;
  contentType: string;
  sizeBytes: number;
  version: number;
}

/** GET /api/documents/{id}/file — mint a fresh download ticket for the document's file. */
export function getDocumentFileTicket(id: string): Promise<DocumentFileTicketDto> {
  return apiGet<DocumentFileTicketDto>(`/api/documents/${id}/file`);
}

/**
 * GET /api/documents/{id}/content — the file's raw bytes, streamed SAME-ORIGIN through the API. Used to
 * load a .docx into the in-app editor: the direct-to-storage SAS url is CORS-blocked for in-browser fetch,
 * so we can't read it client-side. Returns the ArrayBuffer the editor wants.
 */
export async function getDocumentContent(id: string): Promise<ArrayBuffer> {
  const token = await getAuthToken();
  const response = await fetch(`${API_BASE}/api/documents/${encodeURIComponent(id)}/content`, {
    headers: token ? { Authorization: `Bearer ${token}` } : {},
  });
  if (!response.ok) {
    throw new Error(`Could not load document content (${response.status})`);
  }
  return response.arrayBuffer();
}

/**
 * POST /api/documents — create the metadata row. For the full upload (create +
 * ticket + PUT + complete) use uploadDocumentFile from src/lib/uploads.ts.
 */
export function createDocument(request: CreateDocumentRequest): Promise<DocumentDto> {
  return apiPost<DocumentDto>('/api/documents', request);
}

/** DocumentDto -> display CompanyDocument. */
export function mapDocument(dto: DocumentDto): CompanyDocument {
  return {
    id: dto.id,
    name: dto.name,
    category: categoryToKebab(dto.category),
    status: statusToKebab(dto.status),
    summary: dto.summary ?? '',
    // reeve's assessment ("Checked today") arrives with the assessment phase.
    lastChecked: undefined,
    uploaded: dto.file ? `Uploaded ${formatDate(dto.file.uploadedAt)}` : undefined,
    fileName: dto.file?.originalName,
    // findings/history/excerpts arrive with the assessment phase.
    findings: [],
    // templateAvailable arrives with the template-catalog phase.
    templateAvailable: false,
    // linkedLawChangeIds arrive with the law-watch phase.
    linkedLawChangeIds: undefined,
    history: undefined,
    excerpts: undefined,
  };
}
